import React from 'react';
import { Helmet } from 'react-helmet-async';

const TermsAndConditionsPage = () => {
    return (
        <>
            <Helmet>
                <title>Terms and Conditions - The Host Bros</title>
                <meta name="description" content="Terms and conditions for The Host Bros web hosting services." />
            </Helmet>

            <div className="min-h-screen bg-gray-50">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
                        <div className="text-center mb-8">
                            <h1 className="text-3xl font-bold text-gray-900 mb-4">Terms and Conditions</h1>
                            <p className="text-gray-600">
                                <strong>Last Updated:</strong> June 27, 2025
                            </p>
                        </div>

                        <div className="prose prose-gray max-w-none">
                            <p className="text-lg text-gray-700 mb-6">
                                Welcome to No Big Deal Group LLC! These Terms and Conditions ("Terms") govern your use of our web hosting services, including WordPress hosting, offered through our platform.
                            </p>

                            <p className="text-lg text-gray-700 mb-8">
                                By using our services, you agree to be bound by these Terms. If you do not agree, do not use our services.
                            </p>

                            <div className="space-y-8">
                                <section>
                                    <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. Definitions</h2>
                                    <ul className="space-y-2 text-gray-700">
                                        <li><strong>"Service"</strong> refers to web hosting services, including WordPress hosting, provided by No Big Deal Group LLC.</li>
                                        <li><strong>"Client," "You," or "Your"</strong> refers to the individual or entity using our services.</li>
                                        <li><strong>"Content"</strong> means all data, text, software, images, or other materials uploaded to your hosting account.</li>
                                    </ul>
                                </section>

                                <section>
                                    <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. Service Description</h2>
                                    <p className="text-gray-700">
                                        We provide server space, technical support, and WordPress hosting services intended for personal, commercial, or business websites. We strive to ensure maximum uptime, performance, and security, but we do not guarantee uninterrupted service.
                                    </p>
                                </section>

                                <section>
                                    <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. Eligibility</h2>
                                    <p className="text-gray-700">
                                        You must be at least 18 years old or have the authority to bind your organization to these Terms. You agree to provide accurate and complete information when registering.
                                    </p>
                                </section>

                                <section>
                                    <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. Account Responsibility</h2>
                                    <p className="text-gray-700 mb-3">You are responsible for:</p>
                                    <ul className="space-y-2 text-gray-700 ml-6">
                                        <li>• Maintaining the confidentiality of your credentials.</li>
                                        <li>• All activities conducted under your account.</li>
                                        <li>• Ensuring your website complies with all applicable laws and regulations.</li>
                                    </ul>
                                </section>

                                <section>
                                    <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. Acceptable Use</h2>
                                    <p className="text-gray-700 mb-3">You agree <strong>not</strong> to use the service to:</p>
                                    <ul className="space-y-2 text-gray-700 ml-6">
                                        <li>• Host, promote, or distribute malware or viruses.</li>
                                        <li>• Violate any intellectual property or privacy rights.</li>
                                        <li>• Conduct phishing, spamming, or unauthorized data collection.</li>
                                        <li>• Host adult content, hate speech, or illegal material.</li>
                                    </ul>
                                    <p className="text-gray-700 mt-3">We reserve the right to suspend or terminate accounts for violations.</p>
                                </section>

                                <section>
                                    <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Security and Backups</h2>
                                    <ul className="space-y-2 text-gray-700">
                                        <li>• We implement industry-standard security protocols but cannot guarantee immunity from cyberattacks or data loss.</li>
                                        <li>• You are solely responsible for securing your WordPress installation, including keeping plugins/themes updated and using strong passwords.</li>
                                        <li>• We may offer backup services, but you are ultimately responsible for your data and backups.</li>
                                    </ul>
                                </section>

                                <section>
                                    <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Data Privacy and Compliance</h2>
                                    <p className="text-gray-700">
                                        We process personal data in accordance with our <a href="/privacy-policy" className="text-primary-600 hover:text-primary-500 underline">Privacy Policy</a>. You must ensure that your website's data collection complies with applicable laws (e.g., GDPR, CCPA). We are not liable for any data privacy breaches resulting from your site's configuration.
                                    </p>
                                </section>

                                <section>
                                    <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. Billing and Payment</h2>
                                    <ul className="space-y-2 text-gray-700">
                                        <li>• Fees are billed monthly or annually, depending on your subscription.</li>
                                        <li>• Late or failed payments may result in service suspension.</li>
                                        <li>• All fees are non-refundable unless otherwise stated.</li>
                                    </ul>
                                </section>

                                <section>
                                    <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. Limitation of Liability</h2>
                                    <p className="text-gray-700 mb-3">To the maximum extent permitted by law, No Big Deal Group LLC is <strong>not liable</strong> for:</p>
                                    <ul className="space-y-2 text-gray-700 ml-6">
                                        <li>• Any indirect, incidental, or consequential damages.</li>
                                        <li>• Loss of data, profits, or business.</li>
                                        <li>• Security breaches or hacking of your site.</li>
                                    </ul>
                                    <p className="text-gray-700 mt-3">Our total liability is limited to the fees you paid in the past 3 months.</p>
                                </section>

                                <section>
                                    <h2 className="text-2xl font-semibold text-gray-900 mb-4">10. Indemnification</h2>
                                    <p className="text-gray-700 mb-3">You agree to indemnify and hold harmless No Big Deal Group LLC and its affiliates from any claims, damages, or expenses resulting from:</p>
                                    <ul className="space-y-2 text-gray-700 ml-6">
                                        <li>• Your content or use of the service.</li>
                                        <li>• Your violation of these Terms or any law.</li>
                                    </ul>
                                </section>

                                <section>
                                    <h2 className="text-2xl font-semibold text-gray-900 mb-4">11. Suspension and Termination</h2>
                                    <p className="text-gray-700 mb-3">We may suspend or terminate service without notice if:</p>
                                    <ul className="space-y-2 text-gray-700 ml-6">
                                        <li>• You breach these Terms.</li>
                                        <li>• Your use threatens server performance or security.</li>
                                        <li>• Required by law or legal process.</li>
                                    </ul>
                                    <p className="text-gray-700 mt-3">Upon termination, your account and all hosted data may be deleted.</p>
                                </section>

                                <section>
                                    <h2 className="text-2xl font-semibold text-gray-900 mb-4">12. Modifications</h2>
                                    <p className="text-gray-700">
                                        We reserve the right to modify these Terms at any time. We will notify you of significant changes, and continued use of the service constitutes acceptance of the updated Terms.
                                    </p>
                                </section>

                                <section>
                                    <h2 className="text-2xl font-semibold text-gray-900 mb-4">13. Governing Law</h2>
                                    <p className="text-gray-700">
                                        These Terms are governed by the laws of the State of [Insert State], without regard to its conflict of law provisions.
                                    </p>
                                </section>

                                <section>
                                    <h2 className="text-2xl font-semibold text-gray-900 mb-4">14. Contact Information</h2>
                                    <p className="text-gray-700 mb-3">If you have any questions, please contact us at:</p>
                                    <div className="bg-gray-50 p-4 rounded-lg">
                                        <p className="text-gray-700">
                                            <strong>No Big Deal Group LLC</strong><br/>
                                            <a href="mailto:support@thehostbros.com">support@thehostbros.com</a><br/>
                                        </p>
                                    </div>
                                </section>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default TermsAndConditionsPage;